import {
	Card, CardContent, CardHeader, CardActions,
	Typography, Checkbox, Select, MenuItem, Button,
	Grid, Stack, TextField, Tabs, Tab,
	FormControlLabel, SelectChangeEvent, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import { useAppSelector, useAppDispatch } from '../../../redux-store/hooks';
import { ChangeEvent, useState } from 'react';
import { TAX_TYPE, TaxComponent } from '../../../all-types';
import { addTaxComponent } from '../../../redux-store';
import uuid from "react-uuid";
import { AddCircleOutline, Edit, Delete, Block, Percent, MonetizationOn } from '@mui/icons-material';
import { CommonBtn } from "../../../components";

const defaultTaxOptions: TaxComponent = {
	taxId: '',
	taxName: '',
	applicableTax: 0,
	taxType: TAX_TYPE.PERCENTAGE,
	isActive: false,
	isDefault: false,
	startDate: '',
	endDate: ''
};

const TaxCard = ({ tax }: { tax: TaxComponent }) => (
	<Card sx={{ borderRadius: 2, boxShadow: 3, position: 'relative' }}>
		<CardHeader
			avatar={<MonetizationOn color="primary" />}
			title={tax.taxName}
			subheader={`ID: ${tax.taxId}`}
			sx={{ pb: 0 }}
		/>
		<CardContent>
			<Stack direction="row" spacing={1} alignItems="center" mb={1}>
				<Typography variant="body2">Tax:</Typography>
				<Chip
					icon={<Percent />}
					label={`${tax.applicableTax} ${tax.taxType}`}
					color="info"
					variant="outlined"
					size="small"
				/>
			</Stack>

			<Typography variant="body2">Active: {tax.isActive ? '✅ Yes' : '❌ No'}</Typography>
			<Typography variant="body2">Default: {tax.isDefault ? '🌟 Yes' : 'No'}</Typography>
			<Typography variant="body2">Start: {tax.startDate}</Typography>
			<Typography variant="body2">End: {tax.endDate || '—'}</Typography>
		</CardContent>
		<CardActions sx={{ justifyContent: 'flex-end' }}>
			<IconButton size="small" color="primary" aria-label="edit">
				<Edit />
			</IconButton>
			<IconButton size="small" color="error" aria-label="delete">
				<Delete />
			</IconButton>
			<IconButton size="small" color="warning" aria-label="deactivate">
				<Block />
			</IconButton>
		</CardActions>
	</Card>
);

const ClientTaxList = () => {
	const taxes = useAppSelector(state => state.clientInfo.data.taxManager?.activeTaxList);
	const deactivatedList = useAppSelector(state => state.clientInfo.data.taxManager?.deactiveTaxList);
	const dispatcher = useAppDispatch();

	const [tab, setTab] = useState(0);
	const [newTax, setNewTax] = useState<TaxComponent>(defaultTaxOptions);
	const [open, setOpen] = useState(false);

	const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = event.target;
		setNewTax({ ...newTax, [name]: value });
	};

	const handleSelectChange = (e: ChangeEvent<{ value: TAX_TYPE }> | SelectChangeEvent<TAX_TYPE>) => {
		setNewTax({ ...newTax, taxType: e.target.value as TAX_TYPE });
	};

	const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
		setNewTax({ ...newTax, [e.target.name]: e.target.checked });
	};

	const addTax = async () => {
		await dispatcher(addTaxComponent({
			...newTax, taxId: uuid(), startDate: String(new Date().getTime())
		}));
		setNewTax(defaultTaxOptions);
		setOpen(false);
	};

	const list = tab === 0 ? taxes : deactivatedList;

	return (
		<div>
			<Stack alignItems="flex-end" m={2}>
				<CommonBtn
					variant="contained"
					startIcon={<AddCircleOutline />}
					onClick={() => setOpen(true)}
				>
					Add Tax
				</CommonBtn>
			</Stack>
			<Tabs value={tab} onChange={(e, val) => setTab(val)} sx={{ mb: 2 }}>
				<Tab label="Active Taxes" />
				<Tab label="Deactivated Taxes" />
			</Tabs>

			<Grid container spacing={2}>
				{Object.values(list || {}).map((tax) => (
					<Grid item xs={12} sm={6} md={4} key={tax.taxId}>
						<TaxCard tax={tax} />
					</Grid>
				))}
			</Grid>



			<Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
				<DialogTitle>Add New Tax</DialogTitle>
				<DialogContent>
					<Stack spacing={2} mt={1}>
						<TextField
							name="taxName"
							value={newTax.taxName}
							onChange={handleInputChange}
							label="Tax Name"
							fullWidth
						/>
						<TextField
							name="applicableTax"
							value={newTax.applicableTax}
							onChange={handleInputChange}
							label="Applicable Tax"
							fullWidth
						/>
						<Select
							value={newTax.taxType}
							onChange={handleSelectChange}
							fullWidth
						>
							<MenuItem value={TAX_TYPE.PERCENTAGE}>Percentage</MenuItem>
							<MenuItem value={TAX_TYPE.CASH}>Cash</MenuItem>
						</Select>
						<Stack direction="row" spacing={2}>
							<FormControlLabel
								control={<Checkbox name="isActive" checked={newTax.isActive} onChange={handleCheckboxChange} />}
								label="Active"
							/>
							<FormControlLabel
								control={<Checkbox name="isDefault" checked={newTax.isDefault} onChange={handleCheckboxChange} />}
								label="Default"
							/>
						</Stack>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpen(false)}>Cancel</Button>
					<Button
						variant="contained"
						startIcon={<AddCircleOutline />}
						onClick={addTax}
					>
						Add
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export { ClientTaxList };
